import ProductsAPI from "../api/firebase/ProductsAPI";
import PropertiesAPI from "../api/firebase/PropertiesAPI";
import SlidersAPI from "../api/firebase/SlidersAPI";
import { getCategoryImageUrl } from "./categoryProducts";

export const STOREFRONT_CACHE_KEYS = {
  categories: "inproveda_navbar_categories_v1",
  products: "inproveda_storefront_products_v1",
  properties: "inproveda_storefront_properties_v1",
  sliders: "inproveda_storefront_sliders_v1",
  propertiesSettings: "inproveda_storefront_properties_settings_v1",
  imageUrls: "inproveda_storefront_image_urls_v1",
  updatedAt: "inproveda_storefront_updated_at_v1",
};

let activeStorefrontRequest = null;

const readCache = (key, fallback) => {
  if (typeof window === "undefined") return fallback;

  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeCache = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Unable to cache storefront data for ${key}:`, error);
  }
};

const responseData = (response, cacheKey, fallback = []) => {
  if (response?.success && Array.isArray(response.data)) {
    return response.data;
  }
  return readCache(cacheKey, fallback);
};

const getImageUrls = ({ categories, properties, products, sliders }) => {
  const urls = new Set();
  const addUrl = (url) => {
    if (typeof url === "string" && url.trim()) urls.add(url.trim());
  };

  categories.forEach((category) => addUrl(getCategoryImageUrl(category)));
  properties.forEach((property) => {
    addUrl(property.imageUrl || property.image || property.images?.[0]?.url);
  });
  products.forEach((product) => addUrl(product.images?.[0]?.url));
  sliders.forEach((slider) => {
    if (slider.isActive !== false) addUrl(slider.imageUrl || slider.image);
  });

  return Array.from(urls);
};

const preloadImage = (url) =>
  new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(url);
    };
    const timeoutId = window.setTimeout(finish, 20000);
    image.onload = finish;
    image.onerror = finish;
    image.src = url;
  });

const preloadImages = async (urls) => {
  if (typeof window === "undefined" || typeof Image === "undefined") return;
  await Promise.all(urls.map(preloadImage));
};

const fetchStorefrontData = async (additionalImageUrls = []) => {
  const productsAPI = new ProductsAPI();
  const propertiesAPI = new PropertiesAPI();
  const slidersAPI = new SlidersAPI();

  const [productsResponse, categoriesResponse, propertiesResponse, slidersResponse, settingsResponse] =
    await Promise.all([
      productsAPI.getProducts(),
      productsAPI.getCategories(),
      propertiesAPI.getProperties(),
      slidersAPI.getSliders(),
      propertiesAPI.getSectionSettings(),
    ]);

  const products = responseData(
    productsResponse,
    STOREFRONT_CACHE_KEYS.products
  );
  const categories = responseData(
    categoriesResponse,
    STOREFRONT_CACHE_KEYS.categories
  );
  const properties = responseData(
    propertiesResponse,
    STOREFRONT_CACHE_KEYS.properties
  );
  const sliders = responseData(slidersResponse, STOREFRONT_CACHE_KEYS.sliders);
  const propertiesSettings = settingsResponse?.success
    ? settingsResponse.data
    : readCache(STOREFRONT_CACHE_KEYS.propertiesSettings, {
        isEnabled: true,
      });

  const imageUrls = Array.from(
    new Set([
      ...getImageUrls({ categories, properties, products, sliders }),
      ...additionalImageUrls.filter(Boolean),
    ])
  );

  writeCache(STOREFRONT_CACHE_KEYS.products, products);
  writeCache(STOREFRONT_CACHE_KEYS.categories, categories);
  writeCache(STOREFRONT_CACHE_KEYS.properties, properties);
  writeCache(STOREFRONT_CACHE_KEYS.sliders, sliders);
  writeCache(STOREFRONT_CACHE_KEYS.propertiesSettings, propertiesSettings);
  writeCache(STOREFRONT_CACHE_KEYS.imageUrls, imageUrls);
  writeCache(STOREFRONT_CACHE_KEYS.updatedAt, new Date().toISOString());

  await preloadImages(imageUrls);

  return {
    products,
    categories,
    properties,
    sliders,
    propertiesSettings,
    imageUrls,
  };
};

export const loadStorefrontData = (additionalImageUrls = []) => {
  if (!activeStorefrontRequest) {
    activeStorefrontRequest = fetchStorefrontData(additionalImageUrls).finally(
      () => {
        activeStorefrontRequest = null;
      }
    );
  }
  return activeStorefrontRequest;
};

